import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { RelatoriosContent } from '@/shared/components/relatorios/relatorios-content';
import { PessoaDetalhe } from '@/shared/components/pessoa/pessoa-detalhe/pessoa-detalhe';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaService } from '@/shared/services/pessoa.service';
import { PRESTADOR_RELATORIOS_ROUTES as R } from '@/features/registro/prestadores/prestadores.constants';

/**
 * Shell dos relatórios do prestador (menu de relatórios + detalhe da pessoa). O Relatório por Seleção
 * (`relatorio-selecao-pj`) não gira em torno de um único prestador — permite selecionar vários — então
 * o painel fixo de pessoa é ocultado nessa rota, com o conteúdo ocupando a largura total.
 */
@Component({
    selector: 'app-prestador-relatorios-content',
    standalone: true,
    imports: [RouterModule, RelatoriosContent, PessoaDetalhe],
    template: `
        @if (ocultarPainelPessoa()) {
            <app-relatorios-content [itens]="itens"></app-relatorios-content>
        } @else {
            <div class="grid grid-cols-1 gap-5 items-start lg:grid-cols-[300px_1fr]">
                <div>
                    <app-pessoa-detalhe #pessoaDetalhe [pessoa]="pessoa"></app-pessoa-detalhe>
                </div>
                <div class="min-w-0">
                    <app-relatorios-content [itens]="itens"></app-relatorios-content>
                </div>
            </div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrestadorRelatoriosContent implements OnInit {
    private readonly pessoaService = inject(PessoaService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    itens: MenuItem[] = [];
    pessoa: Pessoa | undefined;

    /** `true` quando a rota filha atual não depende de um único prestador em contexto. */
    readonly ocultarPainelPessoa = signal(this.rotaAtualSemPessoaFixa());

    constructor() {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.ocultarPainelPessoa.set(this.rotaAtualSemPessoaFixa()));
    }

    ngOnInit(): void {
        this.pessoa = this.pessoaService.getPessoa() ?? this.pessoaService.getPessoaReference() ?? undefined;

        this.itens = [
            {
                label: 'Certidão de Alteração Contratual',
                routerLink: R.CERTIDAO_ALTERACAO_CONTRATUAL,
                description: 'Certidão de apresentação de alteração contratual do prestador'
            },
            {
                label: 'Certidão de Responsável Técnico',
                routerLink: R.CERTIDAO_RESPONSAVEL_TECNICO,
                description: 'Certidão de direção técnica do prestador'
            },
            {
                label: 'Certificado de Regularidade',
                routerLink: R.CERTIFICADO_REGULARIDADE,
                description: 'Emite o certificado de regularidade de inscrição do prestador'
            },
            {
                label: 'Declaração de Responsabilidade Técnica',
                routerLink: R.DECLARACAO_RESPONSABILIDADE,
                description: 'Declaração de aceite do cargo de diretor técnico'
            },
            {
                label: 'Declaração de Responsabilidade Técnica - PGRSS',
                routerLink: R.DECLARACAO_RT_PGRSS,
                description: 'Declaração de responsabilidade técnica pelo PGRSS do estabelecimento'
            },
            {
                label: 'Ofício de Deferimento',
                routerLink: R.OFICIO_DEFERIMENTO,
                description: 'Ofício de deferimento do pedido de registro da empresa'
            },
            {
                label: 'Termo de Compromisso de Direção',
                routerLink: R.TERMO_COMPROMISSO_DIRECAO,
                description: 'Termo de compromisso de assunção do cargo de diretor técnico'
            },
            {
                label: 'Termo de Compromisso de Publicidade',
                routerLink: R.TERMO_COMPROMISSO_PUBLICIDADE,
                description: 'Termo de compromisso relativo à publicidade do estabelecimento'
            },
            {
                label: 'Termo de Definição de Prazo',
                routerLink: R.TERMO_DEFINICAO_PRAZO,
                description: 'Termo de compromisso de entrega de documentação pendente (120 dias)'
            },
            {
                label: 'Certidão de Cancelamento',
                routerLink: R.CERTIDAO_CANCELAMENTO,
                description: 'Certidão de cancelamento da inscrição do prestador'
            },
            {
                label: 'Certidão de Diretor Clínico',
                routerLink: R.CERTIDAO_DIRETOR_CLINICO,
                description: 'Certidão de direção clínica do prestador'
            },
            {
                label: 'Certificado de Inscrição',
                routerLink: R.CERTIFICADO_INSCRICAO,
                description: 'Certidão de inscrição de pessoa jurídica'
            },
            {
                label: 'Declaração de Substituição de Diretor Técnico',
                routerLink: R.DECLARACAO_SUBSTITUICAO_DIRETOR,
                description: 'Declaração de aceite do cargo de diretor técnico (substituição)'
            },
            {
                label: 'Certidão de Responsabilidade Técnica (CART)',
                routerLink: R.CART,
                description: 'Certidão de anotação de responsabilidade técnica'
            },
            {
                label: 'Certidão de Suspensão',
                routerLink: R.CERTIDAO_SUSPENSAO,
                description: 'Certidão de suspensão da inscrição do prestador'
            },
            {
                label: 'Ofício de Substituição de Diretor Técnico',
                routerLink: R.OFICIO_SUBSTITUICAO_DIRETOR,
                description: 'Ofício solicitando indicação de novo diretor técnico'
            },
            {
                label: 'Requerimento de Renovação',
                routerLink: R.REQUERIMENTO_RENOVACAO,
                description: 'Requerimento de renovação de certificado de inscrição PJ'
            },
            {
                label: 'Requerimento de Renovação de Regularidade',
                routerLink: R.REQUERIMENTO_RENOVACAO_REGULARIDADE,
                description: 'Requerimento de renovação com certidão de regularidade'
            },
            {
                label: 'Declaração de Dados da Empresa',
                routerLink: R.DECLARACAO_DADOS_EMPRESA,
                description: 'Ficha de declaração de dados cadastrais da empresa'
            },
            {
                label: 'Ficha Cadastral',
                routerLink: R.FICHA_CADASTRAL,
                description: 'Ficha de dados cadastrais completa do prestador'
            },
            {
                label: 'Recibo de Protocolo',
                routerLink: R.RECIBO_PROTOCOLO,
                description: 'Recibo de protocolo de solicitação (por número/ano)'
            },
            {
                label: 'Solicitação de Documentos',
                routerLink: R.SOLICITACAO_DOCUMENTOS,
                description: 'Ofício de solicitação de documentos pendentes'
            },
            {
                label: 'Relatório por Seleção',
                routerLink: R.RELATORIO_SELECAO,
                description: 'Relatório personalizado com campos configuráveis para prestadores selecionados'
            }
        ];
    }

    private rotaAtualSemPessoaFixa(): boolean {
        return this.router.url.includes(R.RELATORIO_SELECAO);
    }
}
